import { useQuery } from "@tanstack/react-query"
import cx from "clsx"
import { format, toZonedTime } from "date-fns-tz"
import { fetchSyncStatus } from "../api"
import { ErrorCallout } from "@rawa/ui/ErrorCallout"
import { Tag } from "@rawa/ui/Tag"
import { NOT_AUTHORIZED_MESSAGE } from "../utils/notAuthorized"
import { isForbidden } from "@rawa/api-client/ApiResponseError"
import typography from "@rawa/ui/styles/typography.module.css"
import styles from "./SyncStatusPage.module.scss"

// The API returns UTC timestamps; every RAWA site is in WA, so render them in
// Perth time with the zone labelled rather than trust the viewer's locale.
const TZ = "Australia/Perth"

function formatDate(iso: string): string {
  return format(toZonedTime(new Date(iso), TZ), "d MMM yyyy, h:mm aaa (zzz)", {
    timeZone: TZ,
  })
}

export function SyncStatusPage() {
  // Read each time the page is shown and never in the background.
  const {
    data,
    isPending: loading,
    isError,
    error,
  } = useQuery({
    queryKey: ["sync-status"],
    queryFn: fetchSyncStatus,
    staleTime: Infinity,
    refetchOnMount: "always",
    // The client's default retries exist for a kiosk tablet waking its Wi-Fi.
    // An administrator at a desk is told what went wrong instead.
    retry: false,
    meta: { source: "fetchSyncStatus", forbiddenIsExpected: true },
  })
  const unsynced = data?.unsynced ?? []
  const recentSuccesses = data?.recentSuccesses ?? []
  const forbidden = isError && isForbidden(error)
  const loadError = isError && !forbidden

  const pending = unsynced.filter((e) => e.status === "pending")
  const failed = unsynced.filter((e) => e.status === "failed")

  return (
    <div className={styles.page}>
      <h1 className={cx(typography.h2, styles.heading)}>Sync Status</h1>
      <p className={styles.subtitle}>
        Feedback responses syncing to ICIS — those still pending or failed, and
        the most recently synced.
      </p>

      {loading && (
        <div role="status" aria-label="Loading entries" className={styles.list}>
          {[0, 1, 2].map((i) => (
            <div key={i} className={styles.card}>
              <div className={styles.body}>
                <span className={styles.skeletonBadge} />
                <div className={styles.info}>
                  <span className={styles.skeletonId} />
                  <span className={styles.skeletonMeta} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && forbidden && (
        <ErrorCallout>{NOT_AUTHORIZED_MESSAGE}</ErrorCallout>
      )}

      {!loading && loadError && (
        <ErrorCallout>Failed to load sync status</ErrorCallout>
      )}

      {!loading &&
        !loadError &&
        !forbidden &&
        pending.length === 0 &&
        failed.length === 0 && (
          <p className={styles.empty}>No pending or failed entries</p>
        )}

      {!loading && pending.length > 0 && (
        <section className={styles.section}>
          <h2 className={cx(typography.h5, styles.sectionHeading)}>
            Pending ({pending.length})
          </h2>
          <ul aria-label="Pending entries" className={styles.list}>
            {pending.map((e) => (
              <li key={e.id} className={styles.card}>
                <div className={styles.body}>
                  <Tag colour="lemon" className={styles.badge}>
                    Pending
                  </Tag>
                  <div className={styles.info}>
                    <span className={styles.id}>{e.feedbackResponseId}</span>
                    <span className={styles.meta}>
                      {formatDate(e.createdAt)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!loading && failed.length > 0 && (
        <section className={styles.section}>
          <h2 className={cx(typography.h5, styles.sectionHeading)}>
            Failed ({failed.length})
          </h2>
          <ul aria-label="Failed entries" className={styles.list}>
            {failed.map((e) => (
              <li key={e.id} className={styles.card}>
                <div className={styles.body}>
                  <Tag colour="peach" className={styles.badge}>
                    Failed
                  </Tag>
                  <div className={styles.info}>
                    <span className={styles.id}>{e.feedbackResponseId}</span>
                    <span className={styles.meta}>
                      {formatDate(e.createdAt)} · {e.attempts} attempt
                      {e.attempts !== 1 ? "s" : ""}
                    </span>
                    {e.lastError && (
                      <span className={styles.error}>{e.lastError}</span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {!loading && recentSuccesses.length > 0 && (
        <section className={styles.section}>
          <h2 className={cx(typography.h5, styles.sectionHeading)}>
            Recently synced ({recentSuccesses.length})
          </h2>
          <ul aria-label="Recently synced" className={styles.list}>
            {recentSuccesses.map((s) => (
              <li key={s.id} className={styles.card}>
                <div className={styles.body}>
                  <Tag colour="mint" className={styles.badge}>
                    Synced
                  </Tag>
                  <div className={styles.info}>
                    <span className={styles.id}>{s.feedbackResponseId}</span>
                    <span className={styles.meta}>
                      {s.icisId ? `ICIS ${s.icisId} · ` : ""}
                      {formatDate(s.syncedAt)}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}
